import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { Chat, useChat } from '@ai-sdk/react'
import {
  lastAssistantMessageIsCompleteWithApprovalResponses,
  type UIMessage,
} from 'ai'
import { RoutesData } from '@/router/RoutesData'
import { tokenManager } from '@/services/TokenManager/TokenManager'
import type { AssistantPageContext } from './contract'
import {
  loadAssistantHistory,
  readAssistantUserId,
  saveAssistantHistory,
} from './history'
import {
  trackAssistantEvent,
  type AssistantEntryPoint,
  type AssistantQuestionSource,
} from './analytics'
import {
  BRIEFING_HOLD_MS,
  useAssistantNudge,
  type AssistantBriefing,
  type AssistantNudge,
} from './nudge'
import {
  getAssistantPageSuggestions,
  type AssistantPageSuggestions,
} from './pageSuggestions'
import { createAssistantTransport } from './transport'
import { useIsSignedIn } from './useIsSignedIn'

export type AssistantMessage = UIMessage

/** A question asked in the top bar; the panel's chat sends it once open. */
export interface AssistantPendingQuestion {
  text: string
  source: AssistantQuestionSource
  /** The case a task suggestion is about, sent as context with it. */
  caseId?: string
}

interface AssistantContextValue {
  /** One conversation shared by the docked panel and the /assistant page. */
  chat: Chat<AssistantMessage>
  /** Signed in and not on /assistant: the top-bar button and panel show. */
  isAvailable: boolean
  isPanelOpen: boolean
  /** What opened the panel this time, for usage counts; null while closed. */
  panelEntryPoint: AssistantEntryPoint | null
  /** Opens the docked panel. Already open: nothing changes. */
  openPanel: (entryPoint: AssistantEntryPoint) => void
  /** Opens the panel and has its chat send `question` (the top-bar field). */
  askQuestion: (
    question: AssistantPendingQuestion,
    entryPoint: AssistantEntryPoint
  ) => void
  /** Asked in the top bar, not sent yet: the chat may still be answering. */
  pendingQuestion: AssistantPendingQuestion | null
  /** The chat has taken the pending question. */
  clearPendingQuestion: () => void
  /**
   * Closes (minimises) the docked panel, also on the way to the full page.
   * The conversation and the briefing stay; a question not sent yet goes.
   */
  closePanel: () => void
  /**
   * There is a conversation to go back to: the chat has messages (an answer
   * may still be coming in) or a briefing card is held. The top-bar button
   * then reopens the panel on it instead of opening the field. A closed
   * briefing is held while its time can be trusted: until a fetch finds its
   * task done or no longer on top, or BRIEFING_HOLD_MS goes by.
   */
  hasConversation: boolean
  /**
   * The nudge's task, shown as a card at the top of the conversation. Screen
   * only: never part of the messages, so the model never sees it.
   */
  briefing: AssistantBriefing | null
  /** Opens the panel on the nudge's briefing card. Asks the model nothing. */
  openBriefing: (nudge: AssistantNudge) => void
  /** Drops the briefing card (the user sent a message). */
  clearBriefing: () => void
  /** Clears the conversation (and the briefing) here and in stored history. */
  startOver: () => void
  /**
   * The urgent task to offer, once "Not Now" and the page rules are applied.
   * Shown under the top-bar button, or as "Needs you".
   */
  nudge: AssistantNudge | null
  /** Review: opens the nudge's briefing and stops offering it. */
  reviewNudge: (nudge: AssistantNudge) => void
  /** Not Now: stops offering the nudge until the app is reloaded. */
  dismissNudge: (nudge: AssistantNudge) => void
  /** Starter questions for the page the user is on. */
  suggestions: AssistantPageSuggestions
  /** The top-bar button, so closing the panel can hand focus back to it. */
  launcherRef: RefObject<HTMLButtonElement>
  /** A top bar with the button is on screen (a few pages have none). */
  hasTopBar: boolean
  /** The top bar says it is on screen; call the returned function on unmount. */
  registerTopBar: () => () => void
}

const AssistantContext = createContext<AssistantContextValue | null>(null)

/** The case the user has open, if they are on a case page. */
const caseIdFromPath = (pathname: string): string | undefined =>
  matchPath(RoutesData.RCase.path, pathname)?.params.id ?? undefined

const isMyTasksPage = (pathname: string) =>
  matchPath(RoutesData.RMyReferrals.path, pathname)?.params.id === 'my-tasks'

const isCasePage = (pathname: string, caseId: string) =>
  matchPath(RoutesData.RCase.path, pathname)?.params.id === caseId

type PanelState = { isOpen: boolean; entryPoint: AssistantEntryPoint | null }

const PANEL_CLOSED: PanelState = { isOpen: false, entryPoint: null }

// Already open: keep how it was opened, so each opening is counted once.
const openFrom = (entryPoint: AssistantEntryPoint) => (current: PanelState) =>
  current.isOpen ? current : { isOpen: true, entryPoint }

/** The briefing card, with what keeps it current. */
interface HeldBriefing {
  /** The nudge's id (task and action), matched against each new fetch. */
  taskId: string
  briefing: AssistantBriefing
  /** When its time was read. */
  readAt: number
}

const isStale = (held: HeldBriefing) =>
  Date.now() - held.readAt >= BRIEFING_HOLD_MS

// Chats the provider has replaced (Clear, a change of user). A request they
// stopped still finishes later, and must not write their messages back.
const retiredChats = new WeakSet<Chat<AssistantMessage>>()

const retireAssistantChat = (chat: Chat<AssistantMessage>) => {
  retiredChats.add(chat)
  chat.stop()
}

/** Exported for tests; the app gets its Chat from the provider. */
export const createAssistantChat = (
  readContext: () => AssistantPageContext,
  userId: string | null,
  initialMessages: AssistantMessage[] = []
) => {
  const chat = new Chat<AssistantMessage>({
    messages: initialMessages,
    transport: createAssistantTransport<AssistantMessage>(readContext),
    // Once every Approve/Cancel card in a turn is answered, send the answers
    // so the server can run (or skip) the changes.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    // Save after every turn, including stopped and failed ones, unless the
    // chat has been replaced since.
    onFinish: ({ messages }) => {
      if (!retiredChats.has(chat)) saveAssistantHistory(userId, messages)
    },
  })
  return chat
}

/**
 * Tells the provider when the chat gets its first message or loses them all
 * (Clear, a dead session), whether or not a chat surface is open. Renders
 * nothing, so a streamed answer re-renders only this, never the provider or
 * the top bar.
 */
const ChatHasMessages = ({
  chat,
  onChange,
}: {
  chat: Chat<AssistantMessage>
  onChange: (hasMessages: boolean) => void
}) => {
  const hasMessages = useChat({ chat }).messages.length > 0
  useEffect(() => onChange(hasMessages), [hasMessages, onChange])
  return null
}

/**
 * Holds the Ask Attorney Share conversation, the docked panel's open state
 * and the proactive nudge. Mounted once in RootLayout, only when the
 * assistant flag is on: the top bar is remounted with every page, so what
 * must survive navigation lives here.
 */
export const AssistantProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation()
  const pageContextRef = useRef<AssistantPageContext>({})

  useEffect(() => {
    pageContextRef.current = { caseId: caseIdFromPath(pathname) }
  }, [pathname])

  const readContext = useCallback(() => pageContextRef.current, [])
  // Tags the stored copy; sign-out wipes it with the rest of the session.
  // Changes with the signed-in user (switchUser below).
  const [userId, setUserId] = useState(() =>
    readAssistantUserId(tokenManager.getAccessToken())
  )
  const [chat, setChat] = useState(() =>
    createAssistantChat(
      readContext,
      userId,
      loadAssistantHistory<AssistantMessage>(userId)
    )
  )
  // The same chat, for callbacks that outlive a render (the session listener).
  const chatRef = useRef(chat)
  const replaceChat = useCallback((next: Chat<AssistantMessage>) => {
    retireAssistantChat(chatRef.current)
    chatRef.current = next
    setChat(next)
  }, [])
  // Starts from the restored history; ChatHasMessages keeps it current.
  const [hasMessages, setHasMessages] = useState(() => chat.messages.length > 0)
  const [panel, setPanel] = useState(PANEL_CLOSED)
  const [pendingQuestion, setPendingQuestion] =
    useState<AssistantPendingQuestion | null>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)
  // A count, so one page's top bar leaving as the next arrives can't race.
  const [topBars, setTopBars] = useState(0)
  const registerTopBar = useCallback(() => {
    setTopBars(count => count + 1)
    return () => setTopBars(count => count - 1)
  }, [])

  const [held, setHeld] = useState<HeldBriefing | null>(null)
  const briefing = held?.briefing ?? null

  const isSignedIn = useIsSignedIn()
  // The full page is the chat itself: no top-bar button or panel there.
  const isAvailable =
    isSignedIn && !matchPath(RoutesData.RAssistant.path, pathname)
  const topTask = useAssistantNudge({
    enabled: isAvailable,
    // Moving on from a page (often the case just handled) checks again.
    refreshOn: pathname,
  })
  // "Not Now" lasts until the app is reloaded.
  const [dismissedNudges, setDismissedNudges] = useState<string[]>([])
  const nudge =
    topTask &&
    !dismissedNudges.includes(topTask.id) &&
    // The task is already in front of them there.
    !isMyTasksPage(pathname) &&
    !isCasePage(pathname, topTask.briefing.caseId)
      ? topTask
      : null
  const suggestions = useMemo(
    () => getAssistantPageSuggestions(pathname, topTask),
    [pathname, topTask]
  )

  const openPanel = useCallback((entryPoint: AssistantEntryPoint) => {
    // The hold timer below can run late (a sleeping laptop): never go back
    // to a briefing whose time is too old to trust.
    if (entryPoint === 'resume') {
      setHeld(current => (current && isStale(current) ? null : current))
    }
    setPanel(openFrom(entryPoint))
  }, [])
  const askQuestion = useCallback(
    (question: AssistantPendingQuestion, entryPoint: AssistantEntryPoint) => {
      setPendingQuestion(question)
      setPanel(openFrom(entryPoint))
    },
    []
  )
  const clearPendingQuestion = useCallback(() => setPendingQuestion(null), [])
  const openBriefing = useCallback((next: AssistantNudge) => {
    trackAssistantEvent({
      event: 'assistant__briefing_opened',
      properties: { action_type: next.briefing.actionType },
    })
    setHeld({ taskId: next.id, briefing: next.briefing, readAt: Date.now() })
    setPanel(openFrom('nudge_review'))
  }, [])
  const clearBriefing = useCallback(() => setHeld(null), [])
  const closePanel = useCallback(() => {
    setPanel(PANEL_CLOSED)
    setPendingQuestion(null)
  }, [])
  const startOver = useCallback(() => {
    replaceChat(createAssistantChat(readContext, userId))
    saveAssistantHistory(userId, [])
    setHasMessages(false)
    setHeld(null)
    setPendingQuestion(null)
  }, [replaceChat, readContext, userId])
  // Ends the conversation on screen (the chat, the briefing, a question not
  // sent yet, the open panel) and starts `nextUserId`'s from their stored
  // copy; null while signed out.
  const switchUser = useCallback(
    (nextUserId: string | null) => {
      // Signed out: nothing to restore.
      const messages = nextUserId
        ? loadAssistantHistory<AssistantMessage>(nextUserId)
        : []
      replaceChat(createAssistantChat(readContext, nextUserId, messages))
      setUserId(nextUserId)
      setHasMessages(messages.length > 0)
      setHeld(null)
      setPendingQuestion(null)
      setPanel(PANEL_CLOSED)
    },
    [replaceChat, readContext]
  )
  const dismissNudge = useCallback(
    (next: AssistantNudge) => setDismissedNudges(ids => [...ids, next.id]),
    []
  )
  const reviewNudge = useCallback(
    (next: AssistantNudge) => {
      // The briefing card: the task's facts, no model call.
      openBriefing(next)
      dismissNudge(next)
    },
    [openBriefing, dismissNudge]
  )

  // A dead session wipes the stored copy with it; end the one on screen at
  // once, so a request still finishing can't write it back.
  useEffect(
    () => tokenManager.onSessionCleared(() => switchUser(null)),
    [switchUser]
  )
  // Signing out and in are in-app navigations, so this provider stays
  // mounted from one user to the next: signing out ends the conversation,
  // signing in starts that user's.
  const wasSignedIn = useRef(isSignedIn)
  useEffect(() => {
    if (wasSignedIn.current === isSignedIn) return
    wasSignedIn.current = isSignedIn
    const nextUserId = isSignedIn
      ? readAssistantUserId(tokenManager.getAccessToken())
      : null
    // Signed in as the user it already holds (the app starting up): keep it.
    if (isSignedIn && nextUserId === userId) return
    switchUser(nextUserId)
  }, [isSignedIn, userId, switchUser])

  // Closing the panel keeps the briefing, so it must not come back out of
  // date. Each fetch of the top task (on moving between pages, or after a
  // notification) brings its time up to date, or drops a closed one whose
  // task is done or no longer on top. The full page has its own rule.
  const isPanelOpen = panel.isOpen
  useEffect(() => {
    if (!isAvailable) return
    setHeld(current => {
      if (!current) return current
      if (topTask?.id !== current.taskId) return isPanelOpen ? current : null
      if (topTask.briefing === current.briefing) return current
      return { ...current, briefing: topTask.briefing, readAt: Date.now() }
    })
  }, [topTask, isPanelOpen, isAvailable])
  // Nothing fetches while the user stays on one page: a closed briefing
  // also goes once its time is too old to trust.
  useEffect(() => {
    if (!held || isPanelOpen || !isAvailable) return undefined
    const timer = setTimeout(
      () => setHeld(current => (current === held ? null : current)),
      held.readAt + BRIEFING_HOLD_MS - Date.now()
    )
    return () => clearTimeout(timer)
  }, [held, isPanelOpen, isAvailable])
  const hasConversation = hasMessages || held !== null

  const value = useMemo(
    () => ({
      chat,
      isAvailable,
      isPanelOpen: panel.isOpen,
      panelEntryPoint: panel.entryPoint,
      openPanel,
      askQuestion,
      pendingQuestion,
      clearPendingQuestion,
      closePanel,
      hasConversation,
      briefing,
      openBriefing,
      clearBriefing,
      startOver,
      nudge,
      reviewNudge,
      dismissNudge,
      suggestions,
      launcherRef,
      hasTopBar: topBars > 0,
      registerTopBar,
    }),
    [
      chat,
      isAvailable,
      panel,
      openPanel,
      askQuestion,
      pendingQuestion,
      clearPendingQuestion,
      closePanel,
      hasConversation,
      briefing,
      openBriefing,
      clearBriefing,
      startOver,
      nudge,
      reviewNudge,
      dismissNudge,
      suggestions,
      topBars,
      registerTopBar,
    ]
  )

  return (
    <AssistantContext.Provider value={value}>
      <ChatHasMessages chat={chat} onChange={setHasMessages} />
      {children}
    </AssistantContext.Provider>
  )
}

/**
 * The assistant's shared state. Returns null outside the provider (the flag
 * is off), so callers can render nothing.
 */
export const useAssistant = () => useContext(AssistantContext)
